import styles from './sms-metric.module.scss';
import { Card, Flex, Spin } from 'antd';
import {
  ChartSmsParam,
  DashboardFilters,
  SmsMetrics,
  SmsMetricsChart,
} from '@/services/dashboard-service/dashboard.service.types';
import { CallMetricCard } from '../call-metric-card/call-metric-card';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';

import { CallMetricHeader } from '../call-metric-header/call-metric-header';
import { CallMetricFilters } from '../call-metric-filters/call-metric-filters';
import { ContentType } from 'recharts/types/component/DefaultLegendContent';
import SectionLayout from '@/layouts/section-layout/section.layout';
import { NoRecords } from '../no-records/no-records';
import { calculateBarchartWidth, getRangeDate } from '@/utils/helpers';
import { calculateDateForXAxis, createSmsMetricCardsData } from './sms-metric.utils';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';
import { CustomRangePicker } from '../custom-range-picker/custom-range-picker';
import dayjs, { Dayjs } from 'dayjs';
import { differenceInDays } from 'date-fns';

interface SmsMetricProps {
  onChangeFilter: (filterValue: DashboardFilters) => void;
  onChangeDateRangeFilter: (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => void;
  filterValue: DashboardFilters;
  isLoading: boolean;
  isChartLoading?: boolean;
  smsMetricData?: SmsMetrics;
  smsMetricChartData?: SmsMetricsChart;
  onCardClick: (chartParam: ChartSmsParam) => void;
  cardButtonClassName: (param?: ChartSmsParam) => string;
  chartParam?: ChartSmsParam;
  onDateRangeClear: () => void;
  dates: [Dayjs | null, Dayjs | null] | null;
  setDates: (dates: [Dayjs | null, Dayjs | null] | null) => void;
}

export const SmsMetric = ({
  onChangeFilter,
  onChangeDateRangeFilter,
  filterValue,
  isLoading,
  isChartLoading,
  smsMetricData,
  smsMetricChartData,
  onCardClick,
  cardButtonClassName,
  chartParam,
  onDateRangeClear,
  dates,
  setDates,
}: SmsMetricProps) => {
  const cardsData = createSmsMetricCardsData(smsMetricData);
  const { newDate, currentDate } = getRangeDate(
    filterValue.time_range || '7d',
    filterValue.start_time,
    filterValue.end_time
  );
  const screenSize = useScreenSize();

  const activeCard =
    smsMetricChartData &&
    cardsData?.find((card) => {
      return card.chartParam === chartParam;
    });

  const activeCardName = activeCard
    ? activeCard.title === 'Average Speed of Answer (ASA)'
      ? 'ASA'
      : activeCard.title
    : '';

  const callNumber = smsMetricData?.inbound_sms.value;

  const maxChartValue = smsMetricData?.chart.reduce((accumulator, currentValue) => {
    const currentMaxValue = Math.max(currentValue.ai, currentValue.agent);
    return Math.max(accumulator, currentMaxValue);
  }, 0);

  const isAiResolutionActive = chartParam === 'ai_resolution';
  const isAgentHandoffActive = chartParam === 'agent_handoff';

  const aiResolutionColor = isAiResolutionActive ? styles.primaryColor : styles.chartValueColor;
  const agentHandoffColor = isAgentHandoffActive ? styles.secondaryColor : styles.chartValueColor;

  const activeColor = isAiResolutionActive
    ? aiResolutionColor
    : isAgentHandoffActive
      ? agentHandoffColor
      : styles.chartValueColor;

  const getLegendPayload = () => {
    if (isChartLoading) {
      return [{ value: 'Loading...', color: activeColor }];
    }
    if (chartParam && activeCardName) {
      return [{ value: activeCardName, color: activeColor }];
    }
    return [
      { value: 'AI', color: styles.primaryDotStrokeColor },
      { value: 'Agent', color: styles.secondaryColor },
    ];
  };

  const renderCustomLegend: ContentType = ({ payload }) => {
    return payload?.map(({ value, color }) => (
      <div key={value}>
        <p>
          <span className={styles.legendLine} style={{ backgroundColor: color }}></span>
          {value}
        </p>
      </div>
    ));
  };

  const renderAreaChart = (data: SmsMetrics | SmsMetricsChart) => {
    const chartData = (data as SmsMetrics)?.chart ?? [];
    const firstChartItem = chartData[0] ?? {};

    const hasAiData = 'ai' in firstChartItem;
    const hasAgentData = 'agent' in firstChartItem;
    const hasValueData = 'value' in (data as SmsMetricsChart).chart[0];

    const isMobile = screenSize.width < 768;

    const chartWidth = calculateBarchartWidth(chartData.length);

    const startDate = filterValue.start_time ? new Date(filterValue.start_time) : null;
    const endDate = filterValue.end_time ? new Date(filterValue.end_time) : null;

    let dateDifference = 0;
    if (startDate && endDate) {
      dateDifference = differenceInDays(endDate, startDate);
    }
    let xAxisInterval = 0;

    if (dateDifference > 35) {
      xAxisInterval = 4;
    } else if (dateDifference === 1 && chartData.length === 49) {
      xAxisInterval = 4;
    }

    const transformedChartData =
      hasAiData && hasAgentData
        ? chartData.map((entry) => ({
            ...entry,
            ai: entry.ai - entry.agent,
          }))
        : chartData;

    return (
      <>
        {isChartLoading ? (
          <Spin className={styles.spinner} size="large" />
        ) : (
          <div className={styles.chartWrapper}>
            <ResponsiveContainer
              width={isMobile ? chartWidth : '100%'}
              height={isMobile ? 300 : '100%'}
              aspect={!isMobile ? 3 : undefined}
            >
              <AreaChart
                data={data.chart}
                style={{ position: 'relative' }}
                margin={{ right: 7, top: 7 }}
              >
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={styles.primaryColor} stopOpacity={1} />
                    <stop offset="95%" stopColor={styles.primaryColor} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={styles.secondaryColor} stopOpacity={1} />
                    <stop offset="95%" stopColor={styles.secondaryColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Line />
                <XAxis
                  dy={5}
                  tickMargin={10}
                  height={70}
                  strokeDasharray=""
                  axisLine={{ stroke: styles.axisLineXColor }}
                  tickLine={{ stroke: '' }}
                  dataKey="date"
                  tickFormatter={(value) =>
                    calculateDateForXAxis(
                      filterValue.time_range,
                      value,
                      filterValue.start_time,
                      filterValue.end_time
                    )
                  }
                  angle={-45}
                  interval={xAxisInterval}
                  tick={{
                    fill: styles.tickColor,
                    textAnchor: 'end',
                  }}
                />

                <YAxis
                  dx={-5}
                  axisLine={{ stroke: styles.axisLineYColor }}
                  tickLine={{ stroke: '' }}
                  tick={{ fill: styles.tickColor }}
                  tickCount={maxChartValue && maxChartValue > 150 ? 10 : undefined}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ stroke: '#fbe2e5', strokeWidth: 2 }}
                  contentStyle={{ fontSize: '13px' }}
                  labelFormatter={(label) => dayjs(label).format('YYYY-MM-DD HH:mm')}
                  formatter={(value, name, props) => {
                    const transformedEntry = transformedChartData.find(
                      (entry) => entry.date === props.payload.date
                    );

                    if (!transformedEntry) {
                      return value;
                    }
                    if (name === 'ai') {
                      return [`${transformedEntry.ai}`, 'AI'];
                    }
                    if (name === 'agent') {
                      return [`${transformedEntry.agent}`, 'Agent'];
                    }
                    return value;
                  }}
                />

                <CartesianGrid strokeDasharray="3 3" fill={styles.whiteBackground} />
                {hasAiData && (
                  <Area
                    activeDot={{ r: 5, stroke: styles.primaryDotColor }}
                    dot={{
                      stroke: styles.primaryDotColor,
                      r: 5,
                      fill: styles.backgroundColor,
                      strokeWidth: 3,
                    }}
                    type="linear"
                    dataKey="ai"
                    stroke={styles.primaryDotStrokeColor}
                    fillOpacity={1}
                    fill="url(#colorUv)"
                  />
                )}
                {hasAgentData && (
                  <Area
                    activeDot={{ r: 5, stroke: styles.secondaryDotColor }}
                    dot={{
                      stroke: styles.secondaryDotColor,
                      r: 5,
                      fill: styles.backgroundColor,
                      strokeWidth: 3,
                    }}
                    type="linear"
                    dataKey="agent"
                    stroke={styles.secondaryColor}
                    fillOpacity={1}
                    fill="url(#colorPv)"
                  />
                )}

                {hasValueData && (
                  <Area
                    activeDot={{ r: 5, stroke: activeColor }}
                    dot={{
                      stroke: activeColor,
                      r: 5,
                      fill: styles.backgroundColor,
                      strokeWidth: 3,
                    }}
                    type="linear"
                    dataKey="value"
                    stroke={activeColor}
                    fillOpacity={1}
                    fill={isAiResolutionActive ? 'url(#colorUv)' : 'url(#colorPv)'}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </>
    );
  };

  return (
    <SectionLayout sectionTitle="Sms metrics" flexClassName={styles.sectionLayout}>
      <Card
        styles={{
          title: { flex: 'none' },
        }}
        loading={isLoading}
        title={
          <CallMetricHeader
            textHeader="sms:"
            calls={smsMetricData?.inbound_sms.value ?? 0}
            change={smsMetricData?.inbound_sms.change ?? 0}
          />
        }
        extra={
          <div className={styles.legends}>
            <Legend
              wrapperStyle={{
                position: 'relative',
                display: 'flex',
                flexWrap: 'nowrap',
                flex: 1,
                gap: 10,
                marginRight: '12px',
              }}
              payload={getLegendPayload()}
              content={renderCustomLegend}
            />
            <Flex className={styles.filtersWrapper}>
              <CallMetricFilters onChange={onChangeFilter} value={filterValue} />
              <CustomRangePicker
                dates={dates}
                setDates={setDates}
                onClear={onDateRangeClear}
                className={
                  filterValue.start_time !== undefined && filterValue.end_time !== undefined
                    ? styles.activeDatePicker
                    : ''
                }
                format="YYYY-MM-DD"
                onChange={onChangeDateRangeFilter}
                value={
                  filterValue.start_time && filterValue.end_time
                    ? [dayjs(filterValue.start_time), dayjs(filterValue.end_time)]
                    : null
                }
              />
            </Flex>
          </div>
        }
        className={styles.container}
      >
        {callNumber ? (
          <>
            <Flex wrap="wrap" className={styles.callMetricsCardsContainer}>
              {cardsData?.map(
                ({
                  title,
                  tooltipContent,
                  currentPercent,
                  change,
                  sms,
                  description,
                  arrowColor,
                  chartParam,
                }) => (
                  <CallMetricCard
                    onClick={() => onCardClick(chartParam)}
                    buttonClassName={cardButtonClassName(chartParam)}
                    arrowColor={arrowColor}
                    key={title}
                    title={title}
                    tooltipContent={tooltipContent}
                    currentPercent={currentPercent}
                    change={change}
                    calls={sms}
                    description={description}
                  />
                )
              )}
            </Flex>
            {smsMetricChartData
              ? renderAreaChart(smsMetricChartData)
              : renderAreaChart(smsMetricData)}
          </>
        ) : (
          <NoRecords
            className={styles.noRecords}
            description={`There are no records to display for the selected date range (${newDate} - ${currentDate})`}
          />
        )}
      </Card>
    </SectionLayout>
  );
};
